import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Collapse,
  Chip,
} from "@mui/material";
import { Lock, ChevronDown, ChevronRight } from "lucide-react";
import type { MenuItem } from "../types/Sidebar.type";
import { CLINICAL_FONT_FAMILY } from "../components/shared/constants";

interface SidebarProps {
  menu: MenuItem[];
  open: boolean;
  onClose: () => void;
  variant?: "temporary" | "permanent";
  width?: number;
}

const DRAWER_WIDTH = 256;

export const Sidebar: React.FC<SidebarProps> = ({
  menu,
  open,
  onClose,
  variant = "temporary",
  width = DRAWER_WIDTH,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    {},
  );

  const handleMenuClick = (item: MenuItem) => {
    if (!item.active) {
      return;
    }
    if (item.children && item.children.length > 0) {
      setOpenGroups((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
      return;
    }
    // Only navigate if there's a valid path (not a group)
    if (item.path?.trim()) {
      navigate(item.path);
      if (variant === "temporary") {
        onClose();
      }
    }
  };

  const isActive = (item: MenuItem): boolean => {
    // Group items should never be marked as active
    if (item.type === "group") {
      return false;
    }

    const path = item.path || "";
    // Skip empty paths
    if (!path.trim()) {
      return false;
    }

    const pathname = location.pathname;

    // Exact match only - no partial matches
    // This ensures /dashboard only matches /dashboard or /dashboard/, not /dashboard/study
    if (pathname === path || pathname === `${path}/`) {
      return true;
    }

    return false;
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Sidebar Header */}
      <Box sx={{ p: 3, pt: 2 }}>
        <Typography
          variant="overline"
          sx={{
            color: "#64748B",
            fontFamily: CLINICAL_FONT_FAMILY,
            fontSize: "0.75rem",
            fontWeight: 500,
            letterSpacing: "0.1em",
          }}
        >
          Navigation
        </Typography>
      </Box>

      <Divider sx={{ mx: 2, mb: 1 }} />

      {/* Menu Items */}
      <List sx={{ flex: 1, px: 2 }}>
        {menu.map((item) => {
          const active = isActive(item);
          const hasChildren =
            Array.isArray(item.children) && item.children.length > 0;
          const groupOpen = !!openGroups[item.id];
          const isGroupHeader = item.type === "group";
          return (
            <Box key={item.id}>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <Tooltip
                  title={!item.active ? "Coming Soon" : ""}
                  placement="right"
                  arrow
                >
                  <Box sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleMenuClick(item)}
                      disabled={!item.active}
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        px: 2,
                        // Only apply blue background if NOT a group header and is active
                        backgroundColor:
                          !isGroupHeader && active ? "#EFF6FF" : "transparent",
                        border:
                          !isGroupHeader && active
                            ? "1px solid #DBEAFE"
                            : "1px solid transparent",
                        opacity: item.active ? 1 : 0.5,
                        "&:hover": {
                          backgroundColor:
                            !isGroupHeader && active
                              ? "#EFF6FF"
                              : !isGroupHeader && item.active
                                ? "#F1F5F9"
                                : "transparent",
                        },
                        "&.Mui-disabled": { opacity: 0.5 },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box sx={{ position: "relative" }}>
                          <item.icon
                            size={20}
                            color={
                              !isGroupHeader && active ? "#0B5FFF" : "#64748B"
                            }
                          />
                          {!item.active && (
                            <Lock
                              size={12}
                              color="#9CA3AF"
                              style={{
                                position: "absolute",
                                bottom: -2,
                                right: -2,
                                backgroundColor: "white",
                                borderRadius: "50%",
                                padding: 1,
                              }}
                            />
                          )}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          color:
                            !isGroupHeader && active ? "#1D4ED8" : "#64748B",
                          fontFamily: CLINICAL_FONT_FAMILY,
                        }}
                      />
                      {item.readOnly && (
                        <Chip
                          label="View Only"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.6875rem",
                            fontWeight: 500,
                            backgroundColor: "#F1F5F9",
                            color: "#64748B",
                            border: "1px solid #E2E8F0",
                            fontFamily: CLINICAL_FONT_FAMILY,
                            mr: hasChildren ? 1 : 0,
                          }}
                        />
                      )}
                      {hasChildren && (
                        <Box sx={{ color: "#94A3B8" }}>
                          {groupOpen ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                        </Box>
                      )}
                    </ListItemButton>
                  </Box>
                </Tooltip>
              </ListItem>
              {hasChildren && (
                <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {(item.children ?? []).map((child) => {
                      const childActive = isActive(child);
                      return (
                        <ListItem
                          key={child.id}
                          disablePadding
                          sx={{ mb: 0.5 }}
                        >
                          <ListItemButton
                            onClick={() => handleMenuClick(child)}
                            disabled={!child.active}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              px: 2,
                              backgroundColor: childActive
                                ? "#EEF2FF"
                                : "transparent",
                              border: childActive
                                ? "1px solid #E0E7FF"
                                : "1px solid transparent",
                              "&:hover": {
                                backgroundColor: childActive
                                  ? "#EEF2FF"
                                  : "#F8FAFC",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <child.icon
                                size={18}
                                color={childActive ? "#4F46E5" : "#94A3B8"}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={child.name}
                              primaryTypographyProps={{
                                fontSize: "0.8125rem",
                                fontWeight: 500,
                                color: childActive ? "#4338CA" : "#64748B",
                                fontFamily: CLINICAL_FONT_FAMILY,
                              }}
                            />
                            {child.readOnly && (
                              <Chip
                                label="Read Only"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.625rem",
                                  fontWeight: 500,
                                  backgroundColor: "#F8FAFC",
                                  color: "#64748B",
                                  border: "1px solid #E2E8F0",
                                  fontFamily: CLINICAL_FONT_FAMILY,
                                  ml: 1,
                                }}
                              />
                            )}
                            {!child.active && (
                              <Chip
                                size="small"
                                label="Soon"
                                sx={{
                                  height: 16,
                                  fontSize: 10,
                                  color: "#64748B",
                                }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      {/* Footer Info */}
      <Box sx={{ p: 2, mt: "auto" }}>
        <Divider sx={{ mb: 2 }} />
        <Typography
          variant="caption"
          sx={{
            color: "#9CA3AF",
            fontFamily: CLINICAL_FONT_FAMILY,
            fontSize: "0.75rem",
            display: "block",
            textAlign: "center",
          }}
        >
          AIClinTrack v1.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        width,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width,
          boxSizing: "border-box",
          backgroundColor: "white",
          borderRight: "1px solid #E2E8F0",
          fontFamily: CLINICAL_FONT_FAMILY,
          ...(variant === "permanent" && {
            position: "relative",
            height: "100vh",
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
